import type {Me} from '@briary/shared'
import {can} from '../../lib/auth'
import {canOpenPortalPath, canUsePortalModule} from '../../lib/portal-modules'
import {canOpenStaffPath, canUseStaffModule} from '../../lib/staff-modules'
import {matchPath} from '../../lib/router'
import {HELP_TOPICS, type HelpTopic} from './catalog'

export type HelpArea='admin'|'portal'
export interface HelpEnvironment {mode?:'preview'|'parallel'|'live'}
export interface HelpLocation {path:string;search:URLSearchParams}

/** Educational eligibility only. Existing server and record-level guards remain authoritative. */
export function canReadHelpTopic(me:Me|null,area:HelpArea,topic:HelpTopic,environment:HelpEnvironment={}):boolean{
  if(!me||!me.areas.includes(area)||!topic.areas.includes(area))return false
  if(topic.portalOnly&&me.areas.includes('admin'))return false
  if(topic.superadminOnly&&me.isSuperadmin!==true)return false
  if(!topic.requirements.every(requirement=>can(me,requirement)))return false
  if(!canOpenStaffPath(me,topic.entryPath)||!canOpenPortalPath(me,topic.entryPath))return false
  if(topic.portalModule&&!canUsePortalModule(me,topic.portalModule))return false
  if(topic.staffModule&&!canUseStaffModule(me,topic.staffModule))return false
  if(topic.actionMode==='live'&&environment.mode!=='live')return false
  if(topic.actionMode==='editable'&&environment.mode!=='live'&&environment.mode!=='parallel')return false
  return true
}

export function helpContextScore(topic:HelpTopic,location:HelpLocation):number{
  let score=0
  for(const context of topic.contexts){
    if(context.tab!==undefined&&location.search.get('tab')!==context.tab)continue
    try{
      if(!matchPath(context.path,location.path))continue
      const specificity=context.path.split('/').filter(Boolean).reduce((sum,part)=>sum+(part==='*'?0:part.startsWith(':')?1:3),0)
      score=Math.max(score,1+specificity+(context.tab!==undefined?100:0))
    }catch{/* A malformed location must not crash Help. */}
  }
  return score
}

/** Filter before searching/ranking, so titles and steps cannot leak across audiences. */
export function helpTopicsFor(me:Me|null,area:HelpArea,location:HelpLocation,query='',environment:HelpEnvironment={}){
  const terms=query.trim().toLocaleLowerCase('en-GB').split(/\s+/).filter(Boolean)
  const allowed=HELP_TOPICS.filter(topic=>canReadHelpTopic(me,area,topic,environment)).filter(topic=>{
    const text=[topic.title,topic.category,...topic.steps,...(topic.keywords??[])].join(' ').toLocaleLowerCase('en-GB')
    return terms.every(term=>text.includes(term))
  })
  const current=allowed.filter(topic=>helpContextScore(topic,location)>0).sort((a,b)=>helpContextScore(b,location)-helpContextScore(a,location))
  return {current,other:allowed.filter(topic=>!current.includes(topic))}
}

export function nextHelpFocus(current:number,count:number,backward:boolean):number{
  if(count<=0)return -1
  if(current<0)return backward?count-1:0
  return (current+(backward?-1:1)+count)%count
}
